import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationState, PaginationActions, PaginationInfo } from './types';

interface PaginationControlsProps {
  state: PaginationState;
  actions: PaginationActions;
  info: PaginationInfo;
}

const PaginationControls = ({ state, actions, info }: PaginationControlsProps) => {
  const { currentPage, totalItems, itemsPerPage } = state;
  const { setCurrentPage, nextPage, previousPage, setItemsPerPage } = actions;
  const { totalPages, startIndex, endIndex, hasNextPage, hasPreviousPage, visiblePages } = info;

  if (totalItems === 0) return null;

  return (
    <div className="flex flex-col items-center justify-between gap-4 py-4 sm:flex-row">
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:justify-start">
        <div className="text-sm text-muted-foreground">
          Showing {startIndex + 1} to {endIndex} of {totalItems} entries
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="itemsPerPage" className="text-sm text-muted-foreground">
            Show:
          </label>
          <Select
            value={String(itemsPerPage)}
            onValueChange={(value) => setItemsPerPage(Number(value))}
          >
            <SelectTrigger id="itemsPerPage" className="h-8 w-[72px]">
              <SelectValue aria-label={`${itemsPerPage} items per page`} />
            </SelectTrigger>
            <SelectContent align="start">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="currentPage" className="text-sm text-muted-foreground">
            Page:
          </label>
          <Select
            value={String(currentPage)}
            onValueChange={(value) => setCurrentPage(Number(value))}
          >
            <SelectTrigger id="currentPage" className="h-8 w-[72px]">
              <SelectValue aria-label={`Page ${currentPage} of ${totalPages}`} />
            </SelectTrigger>
            <SelectContent align="start">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <SelectItem key={page} value={String(page)}>
                  {page}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={previousPage}
          disabled={!hasPreviousPage}
          className="flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        <div className="flex items-center gap-1">
          {visiblePages.map((page) => (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCurrentPage(page)}
              className="min-w-[32px]"
            >
              {page}
            </Button>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={nextPage}
          disabled={!hasNextPage}
          className="flex items-center gap-1"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default PaginationControls;
